import { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import JobCard from './components/JobCard';
import ServiceProviderCard from './components/ServiceProviderCard';
import FilterPanel from './components/FilterPanel';
import SearchBar from './components/SearchBar';
import TagFilter from './components/TagFilter';
import SavedSearches from './components/SavedSearches';
import JobDetailModal from './components/JobDetailModal';
import QuickApplyModal from './components/QuickApplyModal';
import ApplicationTracker from './components/ApplicationTracker';
import { jobsService, applicationsService } from '../../services/supabaseService';
import { workerProfilesService } from '../../services/supabaseService';

const JobBoard = () => {
  const [viewMode, setViewMode] = useState('card');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyingJob, setApplyingJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showApplications, setShowApplications] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('jobs');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  const [filters, setFilters] = useState({
    category: 'all',
    location: 'all',
    budget: { min: '', max: '' },
    datePosted: 'all',
    verifiedOnly: false,
    urgentOnly: false,
    minRating: null,
    availability: 'all',
    verificationLevel: 'all'
  });

  const availableTags = [
  'Plumbing', 'Electrical', 'Carpentry', 'Cleaning', 'Babysitting',
  'Tutoring', 'Driving', 'Cooking', 'Laundry', 'Errands',
  'Tech Support', 'Beauty Services', 'Painting', 'Gardening',
  'Pet Care', 'Moving', 'Repair', 'Installation', 'Maintenance'];


  const mockServiceProviders = [
  {
    id: 1,
    name: 'Juan Dela Cruz',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1766c5fee-1768139783060.png',
    imageAlt: 'Filipino male plumber in blue work uniform holding wrench standing in front of white wall with confident smile',
    category: 'Plumbing',
    rating: 4.9,
    reviews: 156,
    hourlyRate: 350,
    location: 'Quezon City',
    completedJobs: 234,
    isVerified: true,
    bio: 'Licensed plumber with 10+ years experience in residential and commercial plumbing.',
    skills: ['Pipe Repair', 'Installation', 'Emergency Service']
  },
  {
    id: 2,
    name: 'Maria Santos',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b110b4b6-1766961752778.png",
    imageAlt: 'Filipino female electrician in yellow safety vest holding electrical tools with professional demeanor',
    category: 'Electrical',
    rating: 4.8,
    reviews: 142,
    hourlyRate: 400,
    location: 'Manila',
    completedJobs: 198,
    isVerified: true,
    bio: 'Certified electrician specializing in residential wiring and electrical repairs.',
    skills: ['Wiring', 'Circuit Repair', 'Installation']
  },
  {
    id: 3,
    name: 'Pedro Reyes',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19f66120b-1765972724410.png",
    imageAlt: 'Filipino male carpenter in work apron holding hammer and measuring tape with friendly expression',
    category: 'Carpentry',
    rating: 4.7,
    reviews: 128,
    hourlyRate: 380,
    location: 'Makati',
    completedJobs: 176,
    isVerified: true,
    bio: 'Skilled carpenter with expertise in custom furniture and home renovations.',
    skills: ['Custom Furniture', 'Renovation', 'Repair']
  }];


  useEffect(() => {
    if (activeTab === 'jobs') {
      fetchJobs();
    } else {
      fetchWorkers();
    }
  }, [filters, selectedTags, activeTab]);

  useEffect(() => {
    if (searchQuery?.length > 0) {
      const suggestions = generateSearchSuggestions(searchQuery);
      setSearchSuggestions(suggestions);
    } else {
      setSearchSuggestions([]);
    }
  }, [searchQuery]);

  const generateSearchSuggestions = (query) => {
    const lowerQuery = query?.toLowerCase();
    const allSuggestions = [
    ...availableTags,
    'Emergency plumbing',
    'Licensed electrician',
    'Experienced carpenter',
    'Professional cleaning',
    'Certified babysitter',
    'Math tutor',
    'Driver with car',
    'Home cooking',
    'Laundry service'];


    return allSuggestions?.
    filter((s) => s?.toLowerCase()?.includes(lowerQuery))?.
    slice(0, 6);
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await jobsService?.getJobs({
        category: filters?.category,
        location: filters?.location,
        budgetMin: filters?.budget?.min,
        budgetMax: filters?.budget?.max,
        datePosted: filters?.datePosted,
        verifiedOnly: filters?.verifiedOnly,
        urgentOnly: filters?.urgentOnly
      });
      setJobs(data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workerProfilesService?.getWorkers({
        category: filters?.category,
        location: filters?.location,
        rateMin: filters?.budget?.min,
        rateMax: filters?.budget?.max,
        verifiedOnly: filters?.verifiedOnly,
        minRating: filters?.minRating,
        availability: filters?.availability,
        verificationLevel: filters?.verificationLevel,
        skills: selectedTags
      });
      setWorkers(data || []);
    } catch (err) {
      console.error('Error fetching workers:', err);
      setError(err?.message || 'Failed to load workers');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setShowDetailModal(true);
  };

  const handleQuickApply = (job) => {
    setApplyingJob(job);
    setShowApplyModal(true);
  };

  const handleApplySubmit = async (applicationData) => {
    try {
      setLoading(true);
      const result = await applicationsService?.submitApplication(applicationData);

      if (result) {
        setSuccessMessage(`Successfully applied to ${applyingJob?.title}!`);
        setShowApplyModal(false);
        setApplyingJob(null);

        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      alert(err?.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sortedJobs = [...jobs]?.sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b?.createdAt) - new Date(a?.createdAt);
      case 'oldest':
        return new Date(a?.createdAt) - new Date(b?.createdAt);
      case 'budget-high':
        return (b?.budgetMax || 0) - (a?.budgetMax || 0);
      case 'budget-low':
        return (a?.budgetMin || 0) - (b?.budgetMin || 0);
      case 'applicants':
        return (b?.applicantCount || 0) - (a?.applicantCount || 0);
      default:
        return 0;
    }
  });

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleLoadSavedSearch = (savedFilters) => {
    setFilters(savedFilters);
    setSearchQuery(savedFilters?.searchQuery || '');
    setSelectedTags(savedFilters?.selectedTags || []);
  };

  const filteredJobs = sortedJobs?.filter((job) => {
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      const matchesTitle = job?.title?.toLowerCase()?.includes(query);
      const matchesDescription = job?.description?.toLowerCase()?.includes(query);
      const matchesSkills = job?.requiredSkills?.some((skill) =>
      skill?.toLowerCase()?.includes(query)
      );
      if (!matchesTitle && !matchesDescription && !matchesSkills) return false;
    }

    if (selectedTags?.length > 0) {
      const matchesTags = selectedTags?.some((tag) =>
      job?.category?.toLowerCase() === tag?.toLowerCase() ||
      job?.requiredSkills?.some((skill) =>
      skill?.toLowerCase()?.includes(tag?.toLowerCase())
      )
      );
      if (!matchesTags) return false;
    }

    return true;
  });

  const filteredProviders = workers?.filter((worker) => {
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      const matchesName = worker?.userProfiles?.fullName?.toLowerCase()?.includes(query);
      const matchesBio = worker?.bio?.toLowerCase()?.includes(query);
      const matchesSkills = worker?.skills?.some((skill) =>
      skill?.toLowerCase()?.includes(query)
      );
      if (!matchesName && !matchesBio && !matchesSkills) return false;
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-[60px] lg:pt-[60px]">
        <div className="container mx-auto px-4 py-8 lg:py-10">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900 mb-3">
              Job Board & Service Providers
            </h1>
            <p className="text-base text-gray-600">
              Find jobs or discover skilled service providers for your needs
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={handleSearch}
              suggestions={searchSuggestions}
            />
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
              <p className="text-green-700 font-medium">{successMessage}</p>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <div className="lg:w-80 flex-shrink-0 space-y-6">
              <FilterPanel
                filters={filters}
                onFilterChange={setFilters}
                onClearFilters={() => {
                  setFilters({
                    category: 'all',
                    location: 'all',
                    budget: { min: '', max: '' },
                    datePosted: 'all',
                    verifiedOnly: false,
                    urgentOnly: false
                  });
                  setSearchQuery('');
                  setSelectedTags([]);
                }}
              />

              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <TagFilter
                  selectedTags={selectedTags}
                  onTagsChange={setSelectedTags}
                  availableTags={availableTags}
                />
              </div>

              <SavedSearches
                currentFilters={{ ...filters, searchQuery, selectedTags }}
                onLoadSearch={handleLoadSavedSearch}
              />
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {/* Tab Navigation */}
              <div className="flex items-center gap-2 mb-8 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('jobs')}
                  className={`px-6 py-4 font-medium text-sm transition-all duration-200 relative ${
                    activeTab === 'jobs' ? 'text-[#1E73BE]' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon name="Briefcase" size={18} />
                    Available Jobs
                    <span className="px-2.5 py-1 bg-blue-100 text-[#1E73BE] text-xs font-semibold rounded-full">
                      {filteredJobs?.length}
                    </span>
                  </div>
                  {activeTab === 'jobs' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1E73BE]" />
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('providers')}
                  className={`px-6 py-4 font-medium text-sm transition-all duration-200 relative ${
                    activeTab === 'providers' ? 'text-[#FF6A00]' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={18} />
                    Service Providers
                    <span className="px-2.5 py-1 bg-orange-100 text-[#FF6A00] text-xs font-semibold rounded-full">
                      {filteredProviders?.length}
                    </span>
                  </div>
                  {activeTab === 'providers' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF6A00]" />
                  )}
                </button>
              </div>

              {/* Controls Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <Button
                    variant={viewMode === 'card' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('card')}
                    iconName="Grid3x3"
                  >
                    Card
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    iconName="List"
                  >
                    List
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 font-medium">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e?.target?.value)}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="budget-high">Highest Budget</option>
                    <option value="budget-low">Lowest Budget</option>
                    <option value="applicants">Most Applicants</option>
                  </select>
                </div>
              </div>

              {/* Content Area */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Icon name="Loader2" size={48} className="animate-spin text-[#1E73BE] mx-auto mb-4" />
                    <p className="text-gray-600">Loading...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
                  <Icon name="AlertCircle" size={48} className="text-red-600 mx-auto mb-4" />
                  <p className="text-red-700 font-medium mb-2">Error loading data</p>
                  <p className="text-sm text-gray-600 mb-6">{error}</p>
                  <Button onClick={fetchJobs} variant="outline" size="sm">
                    Try Again
                  </Button>
                </div>
              ) : activeTab === 'jobs' ? (
                filteredJobs?.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-16 text-center">
                    <Icon name="Briefcase" size={64} className="text-gray-400 mx-auto mb-6 opacity-50" />
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      No Jobs Found
                    </h3>
                    <p className="text-gray-600 mb-8">
                      Try adjusting your filters or search query
                    </p>
                    <Button
                      onClick={() => {
                        setFilters({
                          category: 'all',
                          location: 'all',
                          budget: { min: '', max: '' },
                          datePosted: 'all',
                          verifiedOnly: false,
                          urgentOnly: false
                        });
                        setSearchQuery('');
                        setSelectedTags([]);
                      }}
                      variant="outline"
                    >
                      Clear All Filters
                    </Button>
                  </div>
                ) : (
                  <div className={viewMode === 'card' ? 'grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8' : 'space-y-6'}>
                    {filteredJobs?.map((job) => (
                      <JobCard
                        key={job?.id}
                        job={job}
                        viewMode={viewMode}
                        onViewDetails={handleViewDetails}
                        onQuickApply={handleQuickApply}
                      />
                    ))}
                  </div>
                )
              ) : filteredProviders?.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-16 text-center">
                  <Icon name="Users" size={64} className="text-gray-400 mx-auto mb-6 opacity-50" />
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    No Service Providers Found
                  </h3>
                  <p className="text-gray-600 mb-8">
                    Try adjusting your filters or search query
                  </p>
                  <Button
                    onClick={() => {
                      setFilters({
                        category: 'all',
                        location: 'all',
                        budget: { min: '', max: '' },
                        datePosted: 'all',
                        verifiedOnly: false,
                        urgentOnly: false
                      });
                      setSearchQuery('');
                      setSelectedTags([]);
                    }}
                    variant="outline"
                  >
                    Clear All Filters
                  </Button>
                </div>
              ) : (
                <div className={viewMode === 'card' ? 'grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8' : 'space-y-6'}>
                  {filteredProviders?.map((provider) => (
                    <ServiceProviderCard
                      key={provider?.id}
                      provider={provider}
                      viewMode={viewMode}
                      onViewProfile={(p) => console.log('View profile:', p)}
                      onContact={(p) => console.log('Contact:', p)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDetailModal && selectedJob &&
      <JobDetailModal
        job={selectedJob}
        onClose={() => setShowDetailModal(false)}
        onApply={() => {
          setShowDetailModal(false);
          handleQuickApply(selectedJob);
        }} />

      }

      {showApplyModal && applyingJob &&
      <QuickApplyModal
        job={applyingJob}
        onClose={() => setShowApplyModal(false)}
        onSubmit={handleApplySubmit} />

      }

      {showApplications &&
      <ApplicationTracker
        applications={[]}
        onClose={() => setShowApplications(false)} />

      }
    </div>);

};

export default JobBoard;